import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getAllFounderTypes } from '../data/founderTypes';

const Layer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 70% 60% at 50% 45%,
    transparent 0%,
    rgba(12, 15, 20, 0.55) 55%,
    rgba(12, 15, 20, 0.92) 100%
  );
`;

const hexToRgb = (hex) => {
  const normalized = hex.replace('#', '');
  const value = parseInt(normalized, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const buildLayout = (width, height, types) => {
  const cx = width * 0.5;
  const cy = height * 0.46;
  const radius = Math.min(width, height) * 0.28;

  return types.map((type, index) => {
    const angle = (index / types.length) * Math.PI * 2 - Math.PI / 2;
    return {
      type,
      baseX: cx + Math.cos(angle) * radius,
      baseY: cy + Math.sin(angle) * radius,
      phase: index * 1.15,
    };
  });
};

const FounderTypeConstellation = () => {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const layoutRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const types = getAllFounderTypes();
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      layoutRef.current = buildLayout(window.innerWidth, window.innerHeight, types);
    };

    const handlePointerMove = (event) => {
      mouseRef.current = {
        x: (event.clientX / window.innerWidth - 0.5) * 2,
        y: (event.clientY / window.innerHeight - 0.5) * 2,
      };
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });

    const draw = (time = 0) => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const layout = layoutRef.current;
      const parallax = reducedMotion ? { x: 0, y: 0 } : mouseRef.current;

      ctx.clearRect(0, 0, width, height);

      const positions = layout.map(({ baseX, baseY, phase, type }) => {
        const drift = reducedMotion
          ? 0
          : Math.sin(time * 0.00045 + phase) * 6;
        const x = baseX + parallax.x * 14 + Math.cos(phase) * drift;
        const y = baseY + parallax.y * 10 + Math.sin(phase) * drift;
        return { x, y, type };
      });

      // Ring connections
      for (let i = 0; i < positions.length; i += 1) {
        const a = positions[i];
        const b = positions[(i + 1) % positions.length];
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Subtle cross-links (every other pair)
      for (let i = 0; i < positions.length; i += 2) {
        const a = positions[i];
        const b = positions[(i + 3) % positions.length];
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Center hub
      const hubX = width * 0.5 + parallax.x * 6;
      const hubY = height * 0.46 + parallax.y * 4;
      for (let i = 0; i < positions.length; i += 1) {
        ctx.beginPath();
        ctx.moveTo(hubX, hubY);
        ctx.lineTo(positions[i].x, positions[i].y);
        ctx.strokeStyle = 'rgba(45, 212, 191, 0.04)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(hubX, hubY, 3, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(45, 212, 191, 0.25)';
      ctx.fill();

      // Type nodes
      positions.forEach(({ x, y, type }) => {
        const rgb = hexToRgb(type.color);
        const glow = reducedMotion ? 8 : 14;

        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.85)`;
        ctx.shadowColor = type.color;
        ctx.shadowBlur = glow;
        ctx.fill();
        ctx.shadowBlur = 0;

        ctx.beginPath();
        ctx.arc(x, y, 11, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.22)`;
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.font = '600 10px Inter, sans-serif';
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`;
        ctx.textAlign = 'center';
        ctx.fillText(type.code, x, y + 22);
      });
    };

    if (reducedMotion) {
      draw(0);
    } else {
      const animate = (time) => {
        draw(time);
        frameRef.current = requestAnimationFrame(animate);
      };
      frameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', handlePointerMove);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  return (
    <Layer aria-hidden="true">
      <Canvas ref={canvasRef} />
      <Vignette />
    </Layer>
  );
};

export default FounderTypeConstellation;
